import { Box, Button, Checkbox, FormLabel } from "@mui/joy";
import { format, parse } from "date-fns";
import { ClimbType } from "griptonite-api-client";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useController, useFormContext, useFormState, useWatch } from "react-hook-form";

import { TaskSet } from "@/core/types";
import { useCompetitionQuery } from "@/hooks/competitions";
import { useGradeSystemChoices } from "@/hooks/grades";
import { useCurrentVenueCircuitLookup, useVenueZonesByClimbType } from "@/hooks/routes";
import { useCurrentVenueQuery, useCurrentVenueUuid } from "@/hooks/venues";

import CheckboxInput from "../forms/CheckboxInput";
import CircuitSelect from "../forms/CircuitSelect";
import FormGroup from "../forms/FormGroup";
import { GradeRangeInput } from "../forms/GradeRangeSlider";
import { PillsSelect } from "../forms/PillsSelect";
import SelectInput from "../forms/SelectInput";
import TextInput, { DateInput, NumberInput, TextareaInput } from "../forms/TextInput";
import { FormSection } from "../RouteForm/RouteFormFields";
import getSetTypeChoices, { getClimbTypeForSetType, SetType } from "../TaskForm/getSetTypeChoices";

const NameInput = () => {
  const startDate = useWatch({ name: "startDate" });
  // const endDate = useWatch({ name: "endDate" });
  const placeholder = startDate ? format(parse(startDate, "yyyy-MM-dd", new Date()), "do MMM") : "";
  return <TextInput label="Name" name="name" placeholder={placeholder} />;
};

const Dates = () => {
  const { setValue } = useFormContext();

  const [displayEndDate, setDisplayEndDate] = useState(false);

  const startDate = useWatch({ name: "startDate" });
  const endDate = useWatch({ name: "endDate" });

  useEffect(() => {
    if (startDate && startDate !== endDate) {
      setDisplayEndDate(true);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (startDate && startDate !== endDate) {
      setDisplayEndDate(true);
    }
    console.log({ startDate, endDate });
    if (endDate < startDate) {
      setValue("endDate", startDate);
    }
  }, [endDate, startDate, setValue]);

  return (
    <Box>
      <DateInput
        label={displayEndDate ? "Start date" : "Date"}
        name="startDate"
        withTime={false}
        minDate={format(new Date(), "yyyy-MM-dd")}
      />
      <Checkbox
        size="sm"
        label="Multiple days?"
        checked={displayEndDate}
        onChange={(e) => {
          setDisplayEndDate(e.target.checked);
          if (!e.target.checked) {
            setValue("endDate", startDate, { shouldDirty: true });
          }
        }}
      />
      <Box
        sx={{
          marginTop: 2,
          ...(!displayEndDate ? { display: "none" } : undefined),
        }}
      >
        <DateInput label="End date" name="endDate" minDate={startDate} withTime={false} />
      </Box>
    </Box>
  );
};

const TypeSelector = () => {
  const { setValue: setFormValue, control } = useFormContext();
  const { field } = useController({ name: "type", control });

  const type = field.value;

  const handleSelect = useCallback(
    (value: SetType | null) => {
      if (!value) {
        return;
      }
      const climbType = getClimbTypeForSetType(value);
      if (climbType !== undefined) {
        setFormValue("climbType", climbType);
      }
      if (value !== "zone") {
        setFormValue("zone", null);
      }
      if (value !== "circuit") {
        setFormValue("circuit", null);
      }
      field.onChange(value);
    },
    [setFormValue, field],
  );

  const currentVenueUuid = useCurrentVenueUuid();
  const { data: venue, isFetched: venueFetched } = useCurrentVenueQuery();
  const { data: zones, isFetched: zonesFetched } = useVenueZonesByClimbType(
    currentVenueUuid,
    "BOULDER",
  );
  const { data: sportZones, isFetched: sportZonesFetched } = useVenueZonesByClimbType(
    currentVenueUuid,
    "SPORT",
  );
  const typeChoices = useMemo(
    () => getSetTypeChoices(venue, zones, sportZones),
    [venue, zones, sportZones],
  );

  useEffect(() => {
    if (venueFetched && zonesFetched && sportZonesFetched && !type) {
      handleSelect(typeChoices[0].value);
    }
  }, [venueFetched, zonesFetched, sportZonesFetched, typeChoices, type, handleSelect]);

  return (
    <FormGroup>
      <FormLabel>Type</FormLabel>
      <PillsSelect
        name="type"
        options={typeChoices}
        value={type}
        onChange={(value) => handleSelect(value as SetType | null)}
      />
    </FormGroup>
  );
};

const ZoneSelector = ({ climbType }: { climbType: ClimbType }) => {
  const { data: zones } = useVenueZonesByClimbType(useCurrentVenueUuid(), climbType);

  return (
    <SelectInput
      name="zone"
      label={climbType === "BOULDER" ? "Zone" : "Sector"}
      options={zones?.map((zone) => ({ name: zone.name, value: zone.uuid })) || []}
    />
  );
};

export const FormButtons = ({ createNew }: { taskSet?: TaskSet; createNew?: boolean }) => {
  const formState = useFormState();
  let saveText = "Create Set";
  if (!createNew) {
    saveText = "Save";
    if (!formState.isDirty) {
      return null;
    }
  }
  if (formState.isSubmitting) {
    saveText = "Saving...";
  }

  return (
    <Box
      sx={{
        display: "flex",
        padding: 1,
        backgroundColor: "white",
      }}
    >
      <Button type="submit" color="primary" loading={formState.isSubmitting} sx={{ flex: 1 }}>
        {saveText}
      </Button>
    </Box>
  );
};

const CircuitSelector = () => {
  const { watch, setValue: setFormValue } = useFormContext();
  const circuitUuid = watch("circuit");
  const circuit = useCurrentVenueCircuitLookup(circuitUuid);
  useEffect(() => {
    if (circuit) {
      setFormValue("gradeSystem", circuit.gradeSystem);
      setFormValue("minGradeValue", circuit.minGradeValue);
      setFormValue("maxGradeValue", circuit.maxGradeValue);
    }
  }, [circuit, setFormValue]);

  return <CircuitSelect name="circuit" />;
};

export const TaskSetForm = ({ createNew }: { taskSet?: TaskSet; createNew?: boolean }) => {
  const { watch, setValue } = useFormContext();
  const type = watch("type");
  const { data: venue } = useCurrentVenueQuery();
  const competitionUuid = watch("competition");
  const { data: competition } = useCompetitionQuery(competitionUuid);

  const climbType = (type && getClimbTypeForSetType(type)) ?? competition?.climbType;
  const gradeSystem = watch("gradeSystem");

  useEffect(() => {
    if (competition) {
      setValue("climbType", competition.climbType);
      setValue("name", competition.name);

      // Get the date of the competition start time in the venue timezone
      const startDate = format(competition.startTime, "yyyy-MM-dd");

      setValue("startDate", startDate);
      setValue("endDate", startDate);
    }
  }, [competition, setValue]);

  let typeItemSelector: ReactNode = null;
  if (type === "circuit") {
    typeItemSelector = <CircuitSelector />;
  } else if (type === "zone" || type === "sportZone") {
    typeItemSelector = <ZoneSelector climbType={type === "zone" ? "BOULDER" : "SPORT"} />;
  }

  const gradeSystemChoices = useGradeSystemChoices(climbType ?? undefined);

  useEffect(() => {
    if (!gradeSystem || !gradeSystemChoices.includes(gradeSystem)) {
      setValue(
        "gradeSystem",
        climbType === "BOULDER" ? venue?.boulderGradeSystem : venue?.sportGradeSystem,
      );
    }
  }, [
    gradeSystem,
    gradeSystemChoices,
    climbType,
    setValue,
    venue?.boulderGradeSystem,
    venue?.sportGradeSystem,
  ]);

  return (
    <>
      <FormSection>
        <NameInput />
      </FormSection>
      <FormSection>
        <Dates />
      </FormSection>
      {createNew ? (
        <>
          {!competition ? (
            <>
              <FormSection>
                <TypeSelector />
              </FormSection>
              {typeItemSelector && (
                <FormSection>
                  {typeItemSelector}
                  <CheckboxInput label="Strip existing" name="stripExisting" />
                </FormSection>
              )}
            </>
          ) : null}
          <FormSection>
            <TextareaInput label="Notes" name="notes" minRows={3} />
          </FormSection>
          {climbType && (
            <>
              <FormSection>
                <GradeRangeInput label="Grade range" gradeSystemChoices={gradeSystemChoices} />
              </FormSection>
              <FormSection>
                <NumberInput label="Number of routes" name="routesCount" />
              </FormSection>
            </>
          )}
          {/* <DevTool control={control} /> */}
        </>
      ) : (
        <FormSection>
          <TextareaInput label="Notes" name="notes" minRows={3} />
        </FormSection>
      )}
    </>
  );
};
